import type { SberAutoLeadPayload } from '@/api/inspection-leads'
import type {
  PartnerLead,
  PartnerLeadRow,
  PartnerLeadsResult,
  PartnerMe,
} from '@/api/partner'
import type { InspectionLeadStatus } from '@/types/inspection-lead'

/** Демо-режим (VITE_USE_MOCK): заявки СберАвто для кабинета партнёра. Имена вымышленные. */

const DAY = 24 * 3600_000
const ago = (days: number, hours = 0) => new Date(Date.now() - days * DAY - hours * 3600_000).toISOString()
const ahead = (days: number, hour: number) => {
  const d = new Date(Date.now() + days * DAY)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

const SBER_STEPS: InspectionLeadStatus[] = [
  'new', 'in_progress', 'oo_review', 'order_created', 'lot_ready', 'auction', 'approval', 'done',
]

function history(upTo: InspectionLeadStatus, startDaysAgo: number): PartnerLead['history'] {
  const idx = SBER_STEPS.indexOf(upTo)
  return SBER_STEPS.slice(0, idx + 1).map((status, i) => ({ status: fold(status), at: ago(startDaysAgo - i * 0.6) }))
}

/** Как partner_status в БД: «В работе ОО» партнёр видит как `in_progress`. */
const fold = (s: InspectionLeadStatus): InspectionLeadStatus => (s === 'oo_review' ? 'in_progress' : s)

function lead(
  no: number,
  status: InspectionLeadStatus,
  base: Partial<PartnerLead> & { client_name: string; vehicle_brand: string; vehicle_model: string },
  days: number,
): PartnerLead {
  const h =
    status === 'cancelled'
      ? [...history('in_progress', days), { status: 'cancelled' as const, at: ago(days - 1) }]
      : status === 'not_sold'
        ? [...history('approval', days), { status: 'not_sold' as const, at: ago(days - 4.5) }]
        : history(status, days)
  status = fold(status)
  return {
    id: `mock-partner-${no}`,
    lead_no: no,
    external_id: String(5510100 + no * 7),
    created_at: h[0].at,
    status,
    status_changed_at: h[h.length - 1].at,
    client_phone: `+7916555${String(1000 + no).slice(-4)}`,
    location: 'Москва',
    vehicle_year: 2016 + (no % 7),
    vehicle_vin: null,
    license_plate: null,
    trade_in_target_vehicle: null,
    inspection_address: null,
    cancel_comment: null,
    contacted_at: null,
    inspection_failed_at: null,
    inspection_failed_reason: null,
    partner_comment: null,
    partner_comment_at: null,
    inspection_at: null,
    report_url: null,
    autoteka_pdf_path: null,
    cm_expert_pdf_path: null,
    cm_expert_pdf_at: null,
    lot: null,
    history: h,
    ...base,
  }
}

const leads: PartnerLead[] = [
  lead(9, 'new', { client_name: 'Смирнова Анна Олеговна', vehicle_brand: 'Kia', vehicle_model: 'Rio' }, 0.1),
  lead(8, 'in_progress', {
    client_name: 'Козлов Игорь Сергеевич', vehicle_brand: 'Toyota', vehicle_model: 'Camry',
    license_plate: 'А123ВС777', contacted_at: ago(0.5),
  }, 1),
  lead(7, 'oo_review', {
    client_name: 'Лебедева Мария Ивановна', vehicle_brand: 'Hyundai', vehicle_model: 'Creta',
    license_plate: 'М456ОР799', contacted_at: ago(1.6), inspection_address: 'Москва, Профсоюзная ул., 56',
  }, 2),
  lead(6, 'order_created', {
    client_name: 'Орлов Дмитрий Викторович', vehicle_brand: 'Hyundai', vehicle_model: 'Palisade',
    license_plate: 'Х431МТ31', vehicle_vin: 'KMHR381ABKU123456', contacted_at: ago(3.5),
    inspection_address: 'Москва, Ленинский пр-т, 90', inspection_at: ahead(1, 15),
    trade_in_target_vehicle: 'Haval Jolion', autoteka_pdf_path: 'demo/autoteka-6.pdf', cm_expert_pdf_path: 'demo/cm-expert-6.pdf', cm_expert_pdf_at: ago(0.4),
    partner_comment: 'Клиент просил перенести осмотр на вечер, согласовали 15:00.', partner_comment_at: ago(0.3),
  }, 4),
  lead(10, 'order_created', {
    client_name: 'Громов Илья Петрович', vehicle_brand: 'Mazda', vehicle_model: 'CX-5',
    license_plate: 'О555ОО177', contacted_at: ago(2.5), inspection_address: 'Москва, Варшавское ш., 120',
    inspection_at: ago(0.5), inspection_failed_at: ago(0.4), inspection_failed_reason: 'клиент не вышел на осмотр, не отвечает',
    partner_comment: 'Пробуем договориться о повторном осмотре.', partner_comment_at: ago(0.3),
  }, 3),
  lead(5, 'lot_ready', {
    client_name: 'Фёдоров Павел Андреевич', vehicle_brand: 'Volkswagen', vehicle_model: 'Tiguan',
    license_plate: 'Е777КХ750', contacted_at: ago(5.5), inspection_at: ago(2),
    report_url: 'https://example.com/report/demo-5',
    lot: {
      status: 'DRAFT', is_final: false, draft_at: ago(0.3), started_at: ahead(1, 12), finished_at: null, lot_url: 'https://example.com/lot/5', protocol_path: null, protocol_at: null,
      start_price: 1_250_000, leader_summa: null, leader_company: null,
    },
  }, 6),
  lead(4, 'auction', {
    client_name: 'Никитина Ольга Петровна', vehicle_brand: 'Skoda', vehicle_model: 'Octavia',
    license_plate: 'В202АА197', contacted_at: ago(7.5), inspection_at: ago(4),
    report_url: 'https://example.com/report/demo-4',
    lot: {
      status: 'ACTIVE', is_final: false, draft_at: ago(1.5), started_at: ago(0.2), finished_at: null, lot_url: 'https://example.com/lot/4', protocol_path: null, protocol_at: null,
      start_price: 1_450_000, leader_summa: 1_480_000, leader_company: 'ООО «Демо Авто»',
    },
  }, 8),
  lead(3, 'not_sold', {
    client_name: 'Чернов Артём Ильич', vehicle_brand: 'Hyundai', vehicle_model: 'Solaris',
    license_plate: 'О909ТТ77', contacted_at: ago(9.5), inspection_at: ago(6),
    report_url: 'https://example.com/report/demo-3', autoteka_pdf_path: 'demo/autoteka-3.pdf',
    lot: {
      status: 'FAILED', is_final: true, draft_at: ago(7), started_at: ago(6.6), finished_at: ago(6.5), lot_url: 'https://example.com/lot/3', protocol_path: 'demo/3.pdf', protocol_at: ago(6.4),
      start_price: 600_000, leader_summa: 965_000, leader_company: null,
    },
  }, 10),
  lead(2, 'done', {
    client_name: 'Морозов Кирилл Юрьевич', vehicle_brand: 'Mazda', vehicle_model: 'CX-5',
    license_plate: 'С555СС50', contacted_at: ago(13.5), inspection_at: ago(10),
    report_url: 'https://example.com/report/demo-2',
    lot: {
      status: 'SUCCESS', is_final: true, draft_at: ago(7.4), started_at: ago(7), finished_at: ago(6.9), lot_url: 'https://example.com/lot/2', protocol_path: 'demo/2.pdf', protocol_at: ago(6.8),
      start_price: 2_500_000, leader_summa: 2_525_000, leader_company: 'ООО «Демо Моторс»',
    },
  }, 14),
  lead(1, 'cancelled', {
    client_name: 'Васильев Роман Олегович', vehicle_brand: 'Lada', vehicle_model: 'Vesta',
    cancel_comment: 'Клиент продал автомобиль сам',
  }, 16),
]

function toRow(l: PartnerLead): PartnerLeadRow {
  return {
    id: l.id, lead_no: l.lead_no, external_id: l.external_id, client_name: l.client_name,
    vehicle_brand: l.vehicle_brand, vehicle_model: l.vehicle_model, vehicle_year: l.vehicle_year,
    license_plate: l.license_plate, status: l.status, status_changed_at: l.status_changed_at,
    created_at: l.created_at,
    has_autoteka: l.autoteka_pdf_path != null, has_protocol: l.lot?.protocol_path != null,
    autoteka_pdf_path: l.autoteka_pdf_path, protocol_path: l.lot?.protocol_path ?? null,
  }
}

const delay = <T,>(v: T) => new Promise<T>((r) => setTimeout(() => r(v), 150))

export const mockPartner = {
  me: (): Promise<PartnerMe> =>
    delay({ pipeline: 'sberauto', pipeline_title: 'СберАвто', company_name: 'СберАвто', can_create: true }),

  list: (p: { search?: string; status?: InspectionLeadStatus | null; limit?: number; offset?: number }): Promise<PartnerLeadsResult> => {
    const q = (p.search ?? '').trim().toLowerCase()
    const found = leads.filter((l) =>
      !q ||
      String(l.lead_no) === q ||
      [l.external_id, l.client_name, l.license_plate, `${l.vehicle_brand} ${l.vehicle_model}`]
        .some((v) => (v ?? '').toLowerCase().includes(q)),
    )
    const counts: PartnerLeadsResult['counts'] = {}
    for (const l of found) counts[l.status] = (counts[l.status] ?? 0) + 1
    const filtered = found
      .filter((l) => !p.status || l.status === p.status)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
    const offset = p.offset ?? 0
    return delay({
      items: filtered.slice(offset, offset + (p.limit ?? 50)).map(toRow),
      total: filtered.length,
      counts,
    })
  },

  get: (id: string): Promise<PartnerLead> => {
    const l = leads.find((x) => x.id === id)
    return l ? delay(l) : Promise.reject(new Error('not_found'))
  },

  upsert: (payload: SberAutoLeadPayload): Promise<{ leadId: string; created: boolean }> => {
    const existing = payload.external_id ? leads.find((l) => l.external_id === payload.external_id) : null
    if (existing) return delay({ leadId: existing.id, created: false })
    const no = Math.max(...leads.map((l) => l.lead_no)) + 1
    const created = lead(no, 'new', {
      client_name: payload.client_name ?? '',
      vehicle_brand: payload.vehicle_brand ?? '',
      vehicle_model: payload.vehicle_model ?? '',
      external_id: payload.external_id,
      client_phone: payload.client_phone,
      location: payload.location,
      vehicle_vin: payload.vehicle_vin,
      license_plate: payload.vehicle_license_plate,
      trade_in_target_vehicle: payload.trade_in_target_vehicle,
      inspection_address: payload.inspection_address,
    }, 0)
    leads.unshift(created)
    return delay({ leadId: created.id, created: true })
  },
}
